import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Floating mini-player that subscribes silently to a PoC channel's
 * audio/video tracks via LiveKit Web SDK.
 *
 * Lifecycle:
 *   - mount: lazy-import livekit-client, build Room, connect with the
 *     hidden-listener token. Attach incoming audio tracks to <audio>
 *     elements appended to a hidden container.
 *   - unmount: disconnect Room, detach tracks (their <audio> elements
 *     are removed by track.detach()), null out state.
 *
 * Volume is applied to every audio element on every render — multiple
 * speakers in a duplex room yield multiple tracks; one slider rules
 * them all.
 *
 * No props for video yet — most channels are poc_voice. If poc_video
 * is published, the SDK's VideoTrack.attach() returns a <video>
 * element; we'd just need to host it inside the panel. Deferred.
 */
export function PocListenerPanel({ token, wsUrl, channelName, onClose }) {
  const roomRef     = useRef(null);
  const audioElsRef = useRef(new Set());
  const videoContainerRef = useRef(null);
  const videoElsRef = useRef(new Map());  // track.sid -> { el, identity }
  const [status, setStatus] = useState('connecting');
  const [error, setError]   = useState(null);
  const [volume, setVolume] = useState(0.8);
  const [participantCount, setParticipantCount] = useState(0);
  const [activeSpeakers, setActiveSpeakers] = useState([]);
  const [videoCount, setVideoCount] = useState(0);

  // Apply volume on change.
  useEffect(() => {
    for (const el of audioElsRef.current) {
      try { el.volume = volume; } catch {}
    }
  }, [volume]);

  // Open a popup window with the live video tile(s) embedded at
  // large size. The window references our same <video> elements
  // via document.adoptNode/appendChild — when the popup closes,
  // we re-adopt the elements back into the panel so the tracks
  // keep playing.
  const openVideoWindow = useCallback(() => {
    if (videoElsRef.current.size === 0) return;
    const popup = window.open('', '_blank',
      'width=900,height=700,toolbar=no,menubar=no,location=no');
    if (!popup) {
      // eslint-disable-next-line no-alert
      alert('Popup blocked — please allow popups for this site.');
      return;
    }
    popup.document.write(`<!doctype html><html><head><title>${
      (channelName || 'PoC').replace(/[<>&]/g, '')} — live video</title>
      <style>
        body { background:#0f172a; margin:0; padding:16px; font-family:system-ui,-apple-system,sans-serif; color:#e2e8f0; }
        .hdr { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
        .grid { display:grid; gap:12px; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }
        .tile { position:relative; background:#020617; border-radius:12px; overflow:hidden; }
        .tile video { width:100%; display:block; }
        .tile .lbl { position:absolute; bottom:8px; left:8px; right:8px; background:rgba(0,0,0,.6); color:#fff; padding:4px 8px; border-radius:6px; font-size:12px; font-weight:500; }
      </style></head><body>
      <div class="hdr"><div><b>🎧 ${(channelName || '').replace(/[<>&]/g, '')}</b> · live video (hidden)</div></div>
      <div class="grid" id="grid"></div>
      </body></html>`);
    popup.document.close();
    const grid = popup.document.getElementById('grid');
    // Adopt the existing video elements (preserves track + playback
    // state). Original panel still has space-y wrappers; we re-parent
    // the inner <video> elements into the popup grid.
    for (const [sid, v] of videoElsRef.current.entries()) {
      const tile = popup.document.createElement('div');
      tile.className = 'tile';
      tile.dataset.sid = sid;
      const adopted = popup.document.adoptNode(v.el);
      tile.appendChild(adopted);
      const lbl = popup.document.createElement('div');
      lbl.className = 'lbl';
      lbl.textContent = v.identity?.slice(0, 16) || 'peer';
      tile.appendChild(lbl);
      grid.appendChild(tile);
    }
    // When popup closes, move <video> elements back to the panel.
    popup.addEventListener('beforeunload', () => {
      for (const [sid, v] of videoElsRef.current.entries()) {
        try {
          const tile = popup.document.querySelector(`[data-sid="${sid}"]`);
          if (!tile) continue;
          const vid = tile.querySelector('video');
          if (vid && v.wrap) {
            // Adopt back into panel.
            const adopted = document.adoptNode(vid);
            // Re-attach class for in-panel sizing.
            adopted.className = 'w-full rounded-lg bg-slate-900 object-cover';
            adopted.style.minHeight = '120px';
            adopted.style.aspectRatio = '16 / 9';
            // Clear any previous content then re-add label + video.
            v.wrap.replaceChildren();
            v.wrap.appendChild(adopted);
            const label = document.createElement('div');
            label.className = 'absolute bottom-1 left-1 right-1 truncate rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white';
            label.textContent = v.identity?.slice(0, 12) || 'peer';
            v.wrap.appendChild(label);
            v.el = adopted;
          }
        } catch {}
      }
    });
  }, [channelName]);

  useEffect(() => {
    let cancelled = false;
    let room = null;

    (async () => {
      try {
        // Lazy import — keeps livekit-client out of the main bundle.
        const lk = await import('livekit-client');
        if (cancelled) return;

        // adaptiveStream:false — we ALWAYS want video subscription
        // regardless of whether the <video> element is on-screen.
        // The default true paused video when the element wasn't
        // visible to the viewport intersection observer (e.g. inside
        // a tile not yet scrolled into view), which made it look
        // like "video doesn't work".
        room = new lk.Room({ adaptiveStream: false, dynacast: false });
        roomRef.current = room;

        room.on(lk.RoomEvent.TrackSubscribed, (track, _pub, participant) => {
          if (track.kind === 'audio') {
            const el = track.attach();
            el.autoplay = true;
            el.volume = volume;
            el.style.display = 'none';
            document.body.appendChild(el);
            audioElsRef.current.add(el);
          } else if (track.kind === 'video') {
            const el = track.attach();
            el.autoplay = true;
            el.playsInline = true;
            el.muted = true;            // audio routed via separate audio el
            el.className = 'w-full rounded-lg bg-slate-900 object-cover';
            el.style.minHeight = '120px';
            el.style.aspectRatio = '16 / 9';
            el.dataset.identity = participant?.identity || '';
            // Wrap in a labeled tile.
            const wrap = document.createElement('div');
            wrap.className = 'relative';
            wrap.dataset.identity = participant?.identity || '';
            wrap.appendChild(el);
            const label = document.createElement('div');
            label.className = 'absolute bottom-1 left-1 right-1 truncate rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white';
            label.textContent = participant?.name || participant?.identity?.slice(0, 12) || 'peer';
            wrap.appendChild(label);
            if (videoContainerRef.current) {
              videoContainerRef.current.appendChild(wrap);
            }
            videoElsRef.current.set(track.sid, { wrap, el, identity: participant?.identity });
            setVideoCount(videoElsRef.current.size);
          }
        });
        room.on(lk.RoomEvent.TrackUnsubscribed, (track) => {
          const els = track.detach();
          for (const el of els) {
            audioElsRef.current.delete(el);
            try { el.remove(); } catch {}
          }
          const v = videoElsRef.current.get(track.sid);
          if (v) {
            try { v.wrap.remove(); } catch {}
            videoElsRef.current.delete(track.sid);
            setVideoCount(videoElsRef.current.size);
          }
        });
        room.on(lk.RoomEvent.ParticipantConnected,
          () => setParticipantCount(room.remoteParticipants.size));
        room.on(lk.RoomEvent.ParticipantDisconnected,
          () => setParticipantCount(room.remoteParticipants.size));
        room.on(lk.RoomEvent.ActiveSpeakersChanged, (speakers) => {
          const names = speakers
            .filter(s => s.identity && !s.identity.startsWith('__coord_listen:')
                                   && !s.identity.startsWith('EG_'))
            .map(s => s.name || s.identity.slice(0, 8));
          setActiveSpeakers(names);
        });
        room.on(lk.RoomEvent.Disconnected, (reason) => {
          if (!cancelled) {
            setStatus('disconnected');
            setError(reason ? `disconnected: ${reason}` : null);
          }
        });

        await room.connect(wsUrl, token, { autoSubscribe: true });
        if (cancelled) {
          await room.disconnect(true);
          return;
        }
        setStatus('connected');
        setParticipantCount(room.remoteParticipants.size);
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setError(String(e?.message || e));
        }
      }
    })();

    return () => {
      cancelled = true;
      // Detach all audio elements.
      for (const el of audioElsRef.current) {
        try { el.remove(); } catch {}
      }
      audioElsRef.current.clear();
      // Detach all video elements.
      for (const v of videoElsRef.current.values()) {
        try { v.wrap.remove(); } catch {}
      }
      videoElsRef.current.clear();
      if (room) {
        try { room.disconnect(true); } catch {}
      }
      roomRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, wsUrl]);

  const statusColor =
    status === 'connected'    ? 'text-emerald-600' :
    status === 'connecting'   ? 'text-amber-600'   :
    status === 'disconnected' ? 'text-mist'        : 'text-rose-600';

  return (
    <div className={`fixed bottom-4 right-4 z-50 rounded-2xl border border-line bg-white shadow-card ${videoCount > 0 ? 'w-96' : 'w-80'}`}>
      <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink min-w-0">
          <i className={`fa-solid ${videoCount > 0 ? 'fa-eye' : 'fa-headphones'} text-violet-600`} aria-hidden="true"></i>
          <span className="truncate">{videoCount > 0 ? 'Watching' : 'Listening'}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-line bg-white px-2 py-1 text-xs text-mist hover:bg-slate-50"
          title="Stop listening"
        >
          <i className="fa-solid fa-stop mr-1" aria-hidden="true"></i>
          Stop
        </button>
      </div>

      <div className="px-3 py-2 space-y-2">
        <div className="text-sm font-medium text-ink truncate" title={channelName}>
          {channelName}
        </div>

        <div className={`text-xs ${statusColor} flex items-center gap-1.5`}>
          {status === 'connecting' && <i className="fa-solid fa-spinner fa-spin"></i>}
          {status === 'connected' &&
            <i className="fa-solid fa-circle text-[7px] animate-pulse"></i>}
          {status === 'disconnected' && <i className="fa-solid fa-circle-stop"></i>}
          {status === 'error' && <i className="fa-solid fa-triangle-exclamation"></i>}
          <span className="uppercase tracking-wide font-medium">{status}</span>
          <span className="ml-1 text-mist">
            · {participantCount} participant{participantCount === 1 ? '' : 's'}
          </span>
        </div>

        {error && (
          <div className="text-xs text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-md px-2 py-1">
            {error}
          </div>
        )}

        {activeSpeakers.length > 0 && (
          <div className="text-xs text-emerald-700 flex items-center gap-1.5">
            <i className="fa-solid fa-microphone"></i>
            <span className="font-medium">Speaking:</span>
            <span className="truncate">{activeSpeakers.join(', ')}</span>
          </div>
        )}

        {/* Video tiles container — populated imperatively when video
            tracks arrive. Stacked vertically; auto-grows the panel. */}
        {videoCount > 0 && (
          <div className="flex items-center justify-between gap-2 text-xs font-medium text-violet-700">
            <span className="flex items-center gap-1.5">
              <i className="fa-solid fa-video"></i>
              {videoCount} video stream{videoCount === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={openVideoWindow}
              className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-100"
              title="Open video stream(s) in a separate window"
            >
              <i className="fa-solid fa-expand"></i>
              Open in window
            </button>
          </div>
        )}
        <div ref={videoContainerRef} className="space-y-1.5 max-h-[60vh] overflow-y-auto"></div>

        <div className="flex items-center gap-2">
          <i className="fa-solid fa-volume-low text-xs text-mist"></i>
          <input
            type="range"
            min={0} max={1} step={0.01}
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="flex-1 accent-violet-600"
          />
          <i className="fa-solid fa-volume-high text-xs text-mist"></i>
        </div>

        <div className="text-[10px] text-mist border-t border-line/60 pt-1.5">
          <i className="fa-solid fa-eye-slash mr-1"></i>
          Hidden mode — invisible to channel participants. Audit-logged.
        </div>
      </div>
    </div>
  );
}
